import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { errors, PresetVersionId, paxRoute } from "../../common";
import { noSuchPreset, teamRefusals } from "./refusals";
import { PresetSlugParams, VersionNumber } from "./schemas";

/** How many weeks the series cover, the current one last. */
export const STATS_WEEKS = 12;

const Count = z.number().int().nonnegative();

export const PresetStats = z
  .strictObject({
    gamesStarted: Count.meta({ description: "Games started from any version, not evaluations." }),
    players: Count.meta({ description: "Everyone who has been in one of those games." }),
    versions: z
      .array(
        z.strictObject({
          versionId: PresetVersionId,
          number: z.number().int().positive(),
          versionNumber: VersionNumber.meta({
            description: "Its number as its authors gave it, as every version shows it.",
          }),
          gamesStarted: Count,
        }),
      )
      .meta({ description: "Newest first." }),
    weeks: z
      .array(
        z.strictObject({
          weekStart: z.iso.date().meta({ description: "The Monday the week began, in UTC." }),
          roundsPlayed: Count,
          actionsSubmitted: Count,
          playersJoined: Count,
          activePlayers: Count.meta({ description: "Players who acted or played a round." }),
        }),
      )
      .meta({ description: `The last ${STATS_WEEKS} weeks, oldest first.` }),
  })
  .meta({ id: "PresetStats", description: "How a preset's published versions are being played." });
export type PresetStats = z.infer<typeof PresetStats>;

export const getStats = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/presets/v1alpha1/{slug}/stats",
      operationId: "presets.stats.get",
      summary: "Read how a preset is being played.",
      description:
        "Open to everyone on the team. Counts its games and players from Postgres, and reads the weekly series from the game events in ClickHouse.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...teamRefusals, ...noSuchPreset })
  .input(z.object({ params: PresetSlugParams }))
  .output(PresetStats);
